// Core Modules
import { ApplicationConfig, importProvidersFrom, provideZonelessChangeDetection } from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter, TitleStrategy, withInMemoryScrolling, withRouterConfig } from '@angular/router';

// External Modules
import { CacheFactory } from 'cachefactory';
import { provideHighcharts } from 'highcharts-angular';
import { NgxPermissionsModule } from 'ngx-permissions';
import { provideUserIdleConfig } from 'angular-user-idle';

// Tokens
import { WINDOW } from '@core/token/tokens';

// Interceptors
import { authInterceptor } from '@core/interceptors/auth.interceptor';

// Strategies
import { CustomTitleStrategy } from '@core/strategies/custom-title-strategies';

// Routes
import { routes } from '@app/app.routes';

// Environment
import { environment } from '@env/environment';

export const appConfig: ApplicationConfig = {
  providers: [
    CacheFactory,
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
      }),
      withRouterConfig({ onSameUrlNavigation: 'reload' })
    ),
    provideHttpClient(withInterceptors([authInterceptor])),
    // Highcharts is ~1 MB and only the chat-admin Ignite Assistant Usage tab draws a chart, so
    // neither entry point is imported at module scope: a static `import * as Highcharts` here
    // would make it a dependency of `main.ts` and put the whole library in the eager initial
    // bundle for every user of the app. Both are dynamic `import()`s, so the bundler gives them
    // their own chunk that is fetched on first chart render.
    //
    // The `esm/` entry points, not the package root: the root is the UMD/CommonJS build (the
    // reason `highcharts` sits in `allowedCommonJsDependencies`), which cannot be tree-shaken.
    //
    // `usage-chart.component.ts`'s theme sets `accessibility: { enabled: true }`, but the core
    // package ships no accessibility behavior — the module has to be registered or Highcharts
    // silently drops the option and warns in the console. It goes through `modules` rather than
    // a side-effect import so the ordering is the library's guarantee rather than ours:
    // `HighchartsChartService.ensureSharedHighcharts` awaits the instance, then awaits every
    // module, before any chart is created. Both resolve the same `esm/highcharts.js` module
    // instance, which is what lets the module register itself against the core the charts use.
    provideHighcharts({
      instance: () => import('highcharts/esm/highcharts').then((module) => module.default),
      modules: () => [import('highcharts/esm/modules/accessibility')],
    }),
    importProvidersFrom(NgxPermissionsModule.forRoot()),
    provideUserIdleConfig({
      idle: environment.SESSION.maxIdleWaitTime,
      timeout: environment.SESSION.logoutNotifyExpiryTime,
      ping: environment.SESSION.refreshSessionInterval,
    }),
    {
      provide: TitleStrategy,
      useClass: CustomTitleStrategy,
    },
    {
      provide: WINDOW,
      useValue: window,
    },
  ],
};
